import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createMonoInvoice } from "../data/pay";
import coinImg from "../assets/178F722D-F252-429C-9773-CB27053B84EF.png";

const BRAND = {
  bg: "#272c39",
  primary: "#7269e3",
  accent: "#98dea3",
};

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

function getAuthToken() {
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "null");
    return auth?.token || null;
  } catch {
    return null;
  }
}

async function authedFetch(path, { method = "GET", body } = {}) {
  const token = getAuthToken();
  if (!token) {
    const err = new Error("No auth token. Please login again.");
    err.status = 401;
    throw err;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function CoinAmount({ value, size = 18, className = "", imgClassName = "" }) {
  const amount = Number.isFinite(Number(value)) ? Number(value).toFixed(0) : "0";

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={coinImg}
        alt=""
        className={`shrink-0 object-contain ${imgClassName}`}
        style={{ width: size, height: size }}
      />
      <span>{amount}</span>
    </span>
  );
}

function InfoTooltip({ text, ariaLabel = "Info" }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs text-white/80 ring-1 ring-white/10 hover:bg-white/15"
        aria-label={ariaLabel}
      >
        i
      </button>

      {open ? (
        <div className="absolute left-1/2 top-8 z-20 w-72 -translate-x-1/2 rounded-2xl bg-[#313747] p-3 text-left text-xs leading-relaxed text-white/85 shadow-xl ring-1 ring-white/10">
          {text}
        </div>
      ) : null}
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="font-medium text-white/90">{q}</span>
        <span className="text-white/50">{open ? "−" : "+"}</span>
      </button>

      {open ? <div className="px-4 pb-4 text-sm leading-relaxed text-white/70">{a}</div> : null}
    </div>
  );
}

export default function Pay() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();

  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const mode = qs.get("mode") || "topup";
  const status = qs.get("status") || "";

  const formatUAH = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "₴0";
    return `₴${Math.round(v)}`;
  };

  const formatCoinPrice = (priceUAH, points) => {
    const price = Number(priceUAH);
    const pts = Number(points);

    if (!Number.isFinite(price) || !Number.isFinite(pts) || pts <= 0) {
      return `₴0.00 / ${t("payTopup.coinUnit", { defaultValue: "coin" })}`;
    }

    return `₴${(price / pts).toFixed(2)} / ${t("payTopup.coinUnit", {
      defaultValue: "coin",
    })}`;
  };

  const [wallet, setWallet] = useState({ balance: 0, currency: "POINTS" });
  const [walletLoading, setWalletLoading] = useState(false);

  const [selectedId, setSelectedId] = useState("700");
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const PACKS = useMemo(
    () => [
      {
        id: "300",
        points: 300,
        priceUAH: 299,
        tagKey: "starter",
        bonus: 0,
      },
      {
        id: "700",
        points: 700,
        priceUAH: 699,
        tagKey: "mostPopular",
        bonus: 0,
      },
      {
        id: "1550",
        points: 1550,
        priceUAH: 1499,
        tagKey: "bestValue",
        bonus: 50,
      },
      {
        id: "3200",
        points: 3200,
        priceUAH: 2999,
        tagKey: "pro",
        bonus: 200,
      },
    ],
    []
  );

  const selected = useMemo(
    () => PACKS.find((p) => p.id === selectedId) || PACKS[1],
    [PACKS, selectedId]
  );

  const loadWallet = async () => {
    setWalletLoading(true);
    try {
      const data = await authedFetch("/api/me/wallet");
      setWallet({
        balance: Number.isFinite(Number(data?.balance)) ? Number(data.balance) : 0,
        currency: data?.currency || "POINTS",
      });
    } catch (e) {
      console.warn("Wallet load failed:", e?.message || e);
      setWallet({ balance: 0, currency: "POINTS" });
    } finally {
      setWalletLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();

    if (status === "success") {
      setToast(
        t("payTopup.toast.success", {
          defaultValue: "Payment completed successfully ✅",
        })
      );
    }

    if (status === "fail") {
      setToast(
        t("payTopup.toast.fail", {
          defaultValue: "Payment failed ❌",
        })
      );
    }
  }, [status, t]);

  const onPayMono = async () => {
    if (!selected) return;

    setPaying(true);
    setToast("");

    try {
      const packId = `p${selected.id}`;
      const { pageUrl } = await createMonoInvoice(packId);
      if (!pageUrl) throw new Error("No pageUrl from server");

      window.location.href = pageUrl;
    } catch (e) {
      const s = e?.status;
      if (s === 401) {
        setToast(
          t("payTopup.toast.needLogin", {
            defaultValue: "Please sign in again.",
          })
        );
      } else if (s === 403) {
        setToast(
          t("payTopup.toast.onlyTutor", {
            defaultValue: "Top-up is available only for specialists.",
          })
        );
      } else {
        setToast(
          e?.message ||
            t("payTopup.toast.failed", {
              defaultValue: "Payment error",
            })
        );
      }
    } finally {
      setPaying(false);
    }
  };

  return (
    <main className="min-h-screen text-white" style={{ background: BRAND.bg }}>
      <div className="mx-auto max-w-6xl px-4 pt-10 pb-24">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-unbounded text-4xl md:text-5xl font-extrabold tracking-tight">
              {t("payTopup.title", { defaultValue: "Top up balance" })}
            </h1>

            <p className="mt-2 max-w-3xl text-white/70">
              {t("payTopup.subtitle", {
                defaultValue:
                  "Top up your balance to unlock client contacts. Coins are charged only when you open a chat or unlock a client contact.",
              })}
            </p>
          </div>

          <button
            onClick={() => nav(-1)}
            className="rounded-2xl bg-white/10 px-4 py-2 hover:bg-white/15"
          >
            ← {t("common.back", { defaultValue: "Back" })}
          </button>
        </div>

        {toast ? (
          <div className="mt-6 rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3 text-sm">
            {toast}
          </div>
        ) : null}

        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-3xl bg-white/5 ring-1 ring-white/10 p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="font-unbounded text-2xl font-bold">
                  {t("payTopup.choosePack", { defaultValue: "Choose top-up amount" })}
                </h2>

                <InfoTooltip
                  ariaLabel={t("common.info", { defaultValue: "Info" })}
                  text={t("payTopup.tooltip", {
                    defaultValue:
                      "Coins are the platform's internal currency. You pay only when you unlock a chat or client contact. Browsing requests is free.",
                  })}
                />
              </div>

              <div className="text-sm text-white/70">
                {t("payTopup.balance", { defaultValue: "Balance" })}:{" "}
                <span className="font-semibold text-white/90">
                  {walletLoading ? "…" : Number(wallet.balance || 0).toFixed(0)}
                </span>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {PACKS.map((p) => {
                const active = p.id === selectedId;
                const tag = t(`payTopup.tags.${p.tagKey}`, { defaultValue: "" });

                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={[
                      "text-left rounded-2xl p-5 ring-1 transition-all duration-200",
                      active
                        ? "bg-[#7269e3]/15 ring-[#7269e3]/60 shadow-[0_0_0_2px_rgba(114,105,227,0.25)]"
                        : "bg-white/5 ring-white/10 hover:bg-white/7 hover:ring-white/20",
                    ].join(" ")}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="inline-flex items-center gap-2 text-xl font-extrabold text-white">
                          <CoinAmount
                            value={p.points}
                            size={26}
                            className="text-white"
                            imgClassName="drop-shadow-sm"
                          />
                        </div>

                        <div className="mt-4 text-4xl font-extrabold leading-none text-white">
                          {formatUAH(p.priceUAH)}
                        </div>

                        <div className="mt-2 text-sm font-medium text-white/55">
                          {formatCoinPrice(p.priceUAH, p.points)}
                        </div>
                      </div>

                      {tag ? (
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] whitespace-nowrap"
                          style={{
                            background: active
                              ? "rgba(114,105,227,0.20)"
                              : "rgba(255,255,255,0.08)",
                            border: `1px solid ${
                              active
                                ? "rgba(114,105,227,0.40)"
                                : "rgba(255,255,255,0.10)"
                            }`,
                          }}
                        >
                          {tag}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-5 space-y-2">
                      <p className="text-sm text-white/82">
                        {t("payTopup.packHint", {
                          defaultValue: "Access to client contacts.",
                        })}
                      </p>

                      <p className="text-sm text-white/60">
                        {t("payTopup.packHintStrong", {
                          defaultValue: "You pay only when unlocking.",
                        })}
                      </p>

                      {p.bonus > 0 ? (
                        <p className="text-sm font-semibold" style={{ color: BRAND.accent }}>
                          {t("payTopup.bonus", {
                            defaultValue: "+{{count}} bonus coins",
                            count: p.bonus,
                          })}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
              <h3 className="font-semibold text-white/90">
                {t("payTopup.faqTitle", { defaultValue: "Frequently asked questions" })}
              </h3>

              <div className="mt-4 space-y-3">
                <FaqItem
                  q={t("payTopup.faq.price.q", {
                    defaultValue: "How much does one request cost?",
                  })}
                  a={t("payTopup.faq.price.a", {
                    defaultValue:
                      "The price depends on the client's budget and platform rules. Coins are charged only when you decide to unlock a lead.",
                  })}
                />

                <FaqItem
                  q={t("payTopup.faq.refund.q", {
                    defaultValue: "Are coins refundable?",
                  })}
                  a={t("payTopup.faq.refund.a", {
                    defaultValue:
                      "Yes, coins may be refunded in cases предусмотренных правилами платформы.",
                  })}
                />

                <FaqItem
                  q={t("payTopup.faq.expire.q", {
                    defaultValue: "Do coins expire?",
                  })}
                  a={t("payTopup.faq.expire.a", {
                    defaultValue: "No, coins remain on your balance and do not expire.",
                  })}
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6">
            <h2 className="font-unbounded text-2xl font-bold">
              {t("payTopup.paymentTitle", { defaultValue: "Payment" })}
            </h2>

            <div className="mt-4 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
              <div className="text-sm text-white/70">
                {t("payTopup.selected", { defaultValue: "Selected" })}
              </div>

              <div className="mt-2">
                <CoinAmount
                  value={selected.points}
                  size={34}
                  className="text-3xl font-extrabold text-white"
                  imgClassName="drop-shadow-md"
                />
              </div>

              <div className="mt-5">
                <div className="text-sm text-white/60">
                  {t("payTopup.toPay", { defaultValue: "To pay" })}
                </div>
                <div className="mt-1 text-5xl font-extrabold leading-none text-white">
                  {formatUAH(selected.priceUAH)}
                </div>
              </div>

              <button
                onClick={onPayMono}
                disabled={paying}
                className="mt-6 w-full rounded-2xl px-4 py-3 font-extrabold text-black disabled:opacity-60"
                style={{ background: BRAND.accent }}
              >
                {paying
                  ? t("payTopup.payBtnLoading", { defaultValue: "Redirecting…" })
                  : t("payTopup.payBtn", { defaultValue: "Pay" })}
              </button>

              <div className="mt-4 space-y-2 text-xs text-white/65">
                <p>
                  {t("payTopup.safePay", {
                    defaultValue: "Secure payment via Monobank.",
                  })}
                </p>
                <p>
                  {t("payTopup.cards", {
                    defaultValue: "Visa and MasterCard are supported.",
                  })}
                </p>
                <p>
                  {t("payTopup.webhookHint", {
                    defaultValue: "Coins are added automatically after payment.",
                  })}
                </p>
              </div>
            </div>

            {mode !== "topup" ? (
              <div className="mt-4 text-xs text-white/50">mode={mode}</div>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}